import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useFinance } from "@/context/FinanceContext";
import { AccountSelector } from "@/components/AccountSelector";
import { useToast } from "@/hooks/use-toast";
import { DateInput } from "@/components/ui/date-input";

const schema = z
  .object({
    originAccountId: z.string().min(1, "Selecione a conta de origem"),
    destinationAccountId: z.string().min(1, "Selecione a conta de destino"),
    value: z.coerce.number().positive("Informe um valor válido"),
    
    issueDate: z.string().optional(),
    paymentDate: z.string().optional(),
  })
  .refine((data) => data.originAccountId !== data.destinationAccountId, {
    message: "Contas de origem e destino devem ser diferentes",
    path: ["destinationAccountId"],
  });

export type TransferFormValues = z.infer<typeof schema>;

export function TransferForm({ onSubmitted }: { onSubmitted?: () => void }) {
  const { accounts, categories, addTransaction } = useFinance();
  const { toast } = useToast();

  // Form state para campos livres  
  const [freeTextValues, setFreeTextValues] = React.useState({
    originAccount: "",
    destinationAccount: ""
  });

  // Refs para navegação por Enter
  const originAccountRef = React.useRef<HTMLInputElement>(null);
  const destinationAccountRef = React.useRef<HTMLInputElement>(null);
  const valueRef = React.useRef<HTMLInputElement>(null);

  const form = useForm<TransferFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      originAccountId: "",
      destinationAccountId: "",
      value: undefined as any,
      
      issueDate: "",
      paymentDate: "",
    },
  });

  // Função para navegação por Enter
  const handleEnterNavigation = (nextRef: React.RefObject<HTMLInputElement | HTMLButtonElement>) => {
    setTimeout(() => {
      nextRef.current?.focus();
    }, 100);
  };

  async function submit(values: TransferFormValues) {
    const origin = accounts.find((a) => a.id === values.originAccountId);
    const destination = accounts.find((a) => a.id === values.destinationAccountId);

    // Buscar categoria "Transferência" válida do Supabase (não local)
    let transferCategory = categories.find((c) => 
      c.name.toLowerCase() === "transferência" && 
      !c.id.startsWith('cat-') // Garantir que não é ID local
    );

    // Se não existir categoria válida, usar qualquer categoria válida do Supabase
    if (!transferCategory) {
      transferCategory = categories.find(c => !c.id.startsWith('cat-'));
    }

    if (!transferCategory?.id) {
      toast({ 
        title: "Erro ao criar transferência", 
        description: "Nenhuma categoria válida encontrada. Por favor, crie uma categoria primeiro.",
        variant: "destructive", 
        duration: 3000 
      });
      return;
    }

    const paymentDateStr = values.paymentDate || new Date().toISOString().split("T")[0];
    const groupId = crypto.randomUUID();

    try {
      await addTransaction({
        description: `Transferência para ${destination?.name ?? "destino"}`,
        value: values.value,
        type: "despesa",
        categoryId: transferCategory.id,
        accountId: values.originAccountId,
        issueDate: paymentDateStr,
        dueDate: paymentDateStr,
        paymentDate: paymentDateStr,
        settled: true,
        parcelMode: undefined,
        parcels: null,
        kind: "transferencia",
        transferGroupId: groupId,
      });

      await addTransaction({
        description: `Transferência de ${origin?.name ?? "origem"}`,
        value: values.value,
        type: "receita",
        categoryId: transferCategory.id,
        accountId: values.destinationAccountId,
        issueDate: paymentDateStr,
        dueDate: paymentDateStr,
        paymentDate: paymentDateStr,
        settled: true,
        parcelMode: undefined,
        parcels: null,
        kind: "transferencia",
        transferGroupId: groupId,
      });

      toast({ title: "✓ Transferência salva", description: "Transferência registrada com sucesso!", duration: 2000 });
      
      form.reset();
      setFreeTextValues({
        originAccount: "",
        destinationAccount: ""
      });
      
      onSubmitted?.();
    } catch (error: any) {
      console.error('❌ Erro ao criar transferência:', error?.message);
      toast({
        title: "Erro ao salvar transferência", 
        description: "Não foi possível salvar a transferência. Tente novamente.",
        variant: "destructive",
        duration: 3000
      });
    }
  }

  return (
    <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        <AccountSelector
          accounts={accounts}
          value={form.watch("originAccountId")}
          onValueChange={(value) => form.setValue("originAccountId", value, { shouldValidate: true })}
          label="Conta de Origem"
          placeholder="Selecione a conta de origem"
          inputRef={originAccountRef}
          onEnterPress={() => handleEnterNavigation(destinationAccountRef)}
          error={form.formState.errors.originAccountId?.message}
        />

        <AccountSelector
          accounts={accounts.filter(a => a.id !== form.watch("originAccountId"))}
          value={form.watch("destinationAccountId")}
          onValueChange={(value) => form.setValue("destinationAccountId", value, { shouldValidate: true })}
          label="Conta de Destino"
          placeholder="Selecione a conta de destino"
          inputRef={destinationAccountRef}
          onEnterPress={() => handleEnterNavigation(valueRef)}
          error={form.formState.errors.destinationAccountId?.message}
        />

        <div className="space-y-2">
          <Label htmlFor="value" className="text-sm font-medium text-foreground">
            Valor *
          </Label>
          <Input
            ref={valueRef}
            type="number"
            step="0.01"
            {...form.register("value")}
            placeholder="0,00"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), form.handleSubmit(submit)())}
            aria-invalid={!!form.formState.errors.value}
            className={cn(form.formState.errors.value && "border-destructive focus-visible:ring-destructive")}
            id="value"
          />
          {form.formState.errors.value && (
            <div className="text-sm text-destructive">{form.formState.errors.value.message}</div>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-foreground">Data de Pagamento</Label>
          <DateInput
            value={form.watch("paymentDate")}
            onChange={(value) => form.setValue("paymentDate", value)}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" className="min-w-32">
          Salvar Transferência
        </Button>
      </div>
    </form>
  );
}
